import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, FileText, ListChecks, MessageSquare, Languages, ArrowRight, CheckCircle2, BarChart3, Award, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

const STUDENT_PERKS = [
  { icon: MessageSquare, title: "24/7 AI Tutor",            desc: "Ask anything, anytime. Step-by-step explanations across every subject." },
  { icon: FileText,      title: "On-demand worksheets",     desc: "Grade-aligned practice with downloadable PDFs and answer keys." },
  { icon: ListChecks,    title: "Auto-graded quizzes",      desc: "Instant feedback so they know what they really know." },
  { icon: Languages,     title: "English & French centers", desc: "Vocab, grammar, conversation prompts — bilingual learning, built-in." },
];

const PARENT_PERKS = [
  { icon: Mail,        title: "Weekly Parent Reports",    desc: "Delivered to your inbox: what they studied, completed, struggled with, and improved on." },
  { icon: BarChart3,   title: "Progress Dashboard",       desc: "Live charts of learning time, subject performance, and trend lines." },
  { icon: Award,       title: "Achievement Badges",       desc: "Streaks, milestones, and subject mastery — visible to you, motivating to them." },
  { icon: ShieldCheck, title: "Strengths & Gaps Detected",desc: "We surface where your child is excelling and where they need help — automatically." },
];

const HIGHLIGHTS = [
  "Free Forever Plan",
  "No Credit Card Required",
  "Weekly Parent Reports",
  "AI Tutor Available 24/7",
  "Worksheets & Quizzes On Demand",
  "English & French Learning Support",
  "Progress Tracking",
  "Achievement Badges",
];

export default function TutorLanding() {
  const { user } = useAuth();
  const cta = user ? "/tutor/onboarding" : "/register?next=/tutor/onboarding";

  return (
    <div data-testid="tutor-landing">
      {/* HERO */}
      <section className="bg-gradient-to-b from-[#FEF2F2] to-white">
        <div className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="label-eyebrow flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#D92D20]" /> Teacher4Me · K-12 AI Learning</div>
            <h1 className="font-display font-black text-4xl md:text-5xl lg:text-6xl mt-2 tracking-tight leading-[1.05]">
              AI Tutoring for Students.<br />
              <span className="text-[#D92D20]">Progress Reports for Parents.</span>
            </h1>
            <p className="text-[#4B5563] mt-5 max-w-xl">
              Students get personalized learning support. Parents get visibility into learning progress,
              strengths and improvement areas — delivered weekly to your inbox.
            </p>
            <div className="mt-6 flex gap-3 flex-wrap">
              <Link to={cta} data-testid="landing-start" className="btn-primary inline-flex items-center gap-2">
                Start Free Forever <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/tutor/pricing" data-testid="landing-pricing" className="btn-outline">View Pricing</Link>
            </div>
            <p className="text-xs text-[#4B5563] mt-3 font-semibold">
              Free Forever · No credit card required · Upgrade anytime.
            </p>
            <ul className="mt-6 text-xs text-[#4B5563] grid grid-cols-2 gap-x-4 gap-y-2 max-w-md">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#064E3B] flex-shrink-0" />{h}</li>
              ))}
            </ul>
          </div>

          {/* Sample parent report preview */}
          <div className="bg-white border border-[#E5E7EB] rounded-3xl p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="text-xs uppercase tracking-widest text-[#D92D20] font-bold">Sample Parent Report</div>
              <span className="text-[10px] bg-[#064E3B]/10 text-[#064E3B] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full">This week</span>
            </div>
            <div className="font-display font-bold text-xl mt-1">Aanya · Grade 6 · Ontario</div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="bg-[#FEF2F2] rounded-xl p-3">
                <div className="text-2xl font-display font-black text-[#D92D20]">4.2h</div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mt-1">Studied</div>
              </div>
              <div className="bg-[#FEF2F2] rounded-xl p-3">
                <div className="text-2xl font-display font-black text-[#D92D20]">12</div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mt-1">Quizzes</div>
              </div>
              <div className="bg-[#FEF2F2] rounded-xl p-3">
                <div className="text-2xl font-display font-black text-[#D92D20]">87%</div>
                <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mt-1">Avg score</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="text-xs font-semibold mb-1.5">Subjects this week</div>
              <div className="space-y-1.5">
                {[["Mathematics", 80], ["English", 92], ["Science", 65]].map(([s, p]) => (
                  <div key={s}>
                    <div className="flex justify-between text-[11px]"><span>{s}</span><b>{p}%</b></div>
                    <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                      <div className="h-full bg-[#064E3B]" style={{ width: `${p}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
              <div className="bg-[#D1FAE5] text-[#064E3B] rounded-lg p-2"><b>Strength:</b> Fractions</div>
              <div className="bg-[#FEE2E2] text-[#7F1D1D] rounded-lg p-2"><b>Needs work:</b> Long division</div>
            </div>
            <div className="text-[10px] text-[#4B5563] mt-3 italic">A version of this report is emailed to you every week.</div>
          </div>
        </div>
      </section>

      {/* TWO-SIDED VALUE */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-10">
          <div className="label-eyebrow">Two sides. One platform.</div>
          <h2 className="font-display font-black text-3xl md:text-4xl mt-1">Students learn. Parents stay informed.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E5E7EB] rounded-3xl p-7" data-testid="value-students">
            <div className="text-xs uppercase tracking-widest text-[#D92D20] font-bold">For students</div>
            <div className="font-display font-bold text-2xl mt-1">A patient AI tutor, on call 24/7.</div>
            <div className="mt-5 space-y-4">
              {STUDENT_PERKS.map((p) => (
                <div key={p.title} className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-[#D92D20]/10 text-[#D92D20] flex items-center justify-center flex-shrink-0"><p.icon className="w-4 h-4" /></div>
                  <div>
                    <div className="font-semibold text-sm">{p.title}</div>
                    <div className="text-xs text-[#4B5563] mt-0.5">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-[#064E3B] text-white rounded-3xl p-7" data-testid="value-parents">
            <div className="text-xs uppercase tracking-widest text-white/70 font-bold">For parents</div>
            <div className="font-display font-bold text-2xl mt-1">Finally — visibility into how learning is going.</div>
            <div className="mt-5 space-y-4">
              {PARENT_PERKS.map((p) => (
                <div key={p.title} className="flex gap-3">
                  <div className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center flex-shrink-0"><p.icon className="w-4 h-4" /></div>
                  <div>
                    <div className="font-semibold text-sm">{p.title}</div>
                    <div className="text-xs text-white/70 mt-0.5">{p.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#F9FAFB] border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="text-center mb-10">
            <div className="label-eyebrow">How it works</div>
            <h2 className="font-display font-black text-3xl md:text-4xl mt-1">From sign-up to first parent report — in one week.</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Create student account", d: "Free Forever. No credit card." },
              { n: "02", t: "Add parent email",       d: "Optional — but unlocks weekly progress reports." },
              { n: "03", t: "Pick grade & subjects",  d: "Canadian K-12 curriculum, English or French." },
              { n: "04", t: "Learn & get reports",    d: "AI tutoring daily · parent report every Sunday." },
            ].map((s) => (
              <div key={s.n} className="bg-white rounded-2xl p-5 border border-[#E5E7EB]">
                <div className="text-[#D92D20] font-display font-black text-3xl">{s.n}</div>
                <div className="font-display font-bold text-base mt-1">{s.t}</div>
                <div className="text-xs text-[#4B5563] mt-1">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#064E3B] text-white">
        <div className="max-w-4xl mx-auto px-6 py-14 text-center">
          <h2 className="font-display font-black text-3xl md:text-4xl">Start free. Stay informed.</h2>
          <p className="text-sm text-white/80 mt-2 max-w-xl mx-auto">
            Create your free account in under 60 seconds. Add a parent email to start receiving weekly progress reports.
          </p>
          <Link to={cta} data-testid="landing-cta-bottom" className="btn-primary inline-flex items-center gap-2 mt-6">
            Start Free Forever <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="text-[11px] text-white/60 mt-3">No credit card required · Upgrade to Premium Unlimited for $9.99/mo, anytime.</p>
        </div>
      </section>
    </div>
  );
}
